<script>
import { supabase } from "../lib/supabase"
import logo from "../assets/logo.png"
import { deleteCategoryBranch, sortRows } from "../lib/categoryHierarchy"

export default {
  data() {
    return {
      logoUrl: logo,
      menu: [],
      loading: false,
      error: null,
      tapCount: 0,
      tapTimer: null,
      session: null,
      actionError: null,
    }
  },
  mounted() {
    this.fetchMenu()
    this.initAuth()
  },
  beforeUnmount() {
    if (this.tapTimer) {
      clearTimeout(this.tapTimer)
    }
  },
  computed: {
    isAuthed() {
      return !!this.session
    },
    orderedMenu() {
      return sortRows(this.menu)
    },
  },
  methods: {
    async initAuth() {
      const { data } = await supabase.auth.getSession()
      this.session = data.session
      supabase.auth.onAuthStateChange((_event, session) => {
        this.session = session
      })
    },
    async fetchMenu() {
      this.loading = true
      this.error = null

      const { data, error } = await supabase
        .from("categories")
        .select("id,title,cover,sort,parent_id")
        .is("parent_id", null)
        .order("sort", { ascending: true })

      if (error) {
        this.error = "Menü yüklenemedi."
        this.menu = []
      } else {
        this.menu = data || []
      }

      this.loading = false
    },
    async reindexMenu(rows) {
      const ordered = rows || []
      const changes = ordered.reduce((acc, row, index) => {
        const target = index + 1
        const current = Number(row.sort) || 0
        if (current === target) return acc
        acc.push({ id: row.id, sort: target })
        return acc
      }, [])

      if (!changes.length) return true

      const finalUpdates = changes.map(change => {
        return supabase
          .from("categories")
          .update({ sort: change.sort })
          .select("id,sort")
          .eq("id", change.id)
      })

      const finalResults = await Promise.all(finalUpdates)
      const finalErr = finalResults.find(result => result.error)
      if (finalErr?.error) {
        this.actionError = `Kategori sirasi guncellenemedi: ${finalErr.error.message}`
        return false
      }

      const noRowUpdated = finalResults.find(result => !(result.data && result.data.length))
      if (noRowUpdated) {
        this.actionError = "Kategori sirasi guncellenemedi: izin (RLS) veya kimlik doğrulama sorunu olabilir."
        return false
      }

      return true
    },
    canMoveCategory(category, delta) {
      const index = this.orderedMenu.findIndex(item => item.id === category.id)
      if (index === -1) return false
      const target = index + delta
      return target >= 0 && target < this.orderedMenu.length
    },
    async moveCategory(category, delta) {
      const ordered = sortRows(this.orderedMenu)
      const index = ordered.findIndex(item => item.id === category.id)
      if (index === -1) return

      const target = index + delta
      if (target < 0 || target >= ordered.length) return

      const swapped = [...ordered]
      ;[swapped[index], swapped[target]] = [swapped[target], swapped[index]]
      this.actionError = null
      const reordered = await this.reindexMenu(swapped)
      if (!reordered) return
      await this.fetchMenu()
    },
    handleSecretTap() {
      this.tapCount += 1

      if (this.tapCount >= 7) {
        this.tapCount = 0
        if (this.tapTimer) clearTimeout(this.tapTimer)
        this.$router.push("/admin")
        return
      }

      if (this.tapTimer) clearTimeout(this.tapTimer)
      this.tapTimer = setTimeout(() => {
        this.tapCount = 0
      }, 3500)
    },
    scrollToMenu() {
      const el = this.$refs.menuList
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    },
    async deleteCategory(cat) {
      if (!confirm("Kategori silinsin mi?")) return
      this.actionError = null

      const { error } = await deleteCategoryBranch(supabase, cat.id)
      if (error) {
        this.actionError = error
        return
      }

      this.fetchMenu()
    },
  },
}
</script>

<template>
  <div class="home">
    <div class="decor-top" aria-hidden="true"></div>
    <div class="decor-bottom" aria-hidden="true"></div>
    <div class="hero">
      <div class="brand" @click="handleSecretTap">
        <img
          class="brand-logo"
          :src="logoUrl"
          alt="Erdek Deep Cafe & Restaurant"
        />
      </div>
      <!-- <p class="subtitle">// Cafe & Restaruant //</p> -->
      <div class="hero-actions">
        <button class="menu-link" @click="scrollToMenu">
          Menü
        </button>
        <button class="about-link" @click="$router.push('/about')">
          Hakkımızda
        </button>
      </div>
    </div>

    <div v-if="loading" class="state">Yükleniyor...</div>
    <div v-else-if="error" class="state">{{ error }}</div>
    <div v-else-if="actionError" class="state">{{ actionError }}</div>
    <div v-else ref="menuList">
      <div
        v-for="cat in orderedMenu"
        :key="cat.id"
        class="category-card"
        @click="$router.push(`/category/${cat.id}`)"
      >
        <img :src="cat.cover" />
        <div class="order-badges" v-if="isAuthed">
              <button
                class="order-badge"
                @click.stop="moveCategory(cat, -1)"
                aria-label="Üst sıraya al"
                :disabled="!canMoveCategory(cat, -1)"
              >
                ↑
              </button>
              <button
                class="order-badge"
                @click.stop="moveCategory(cat, 1)"
                aria-label="Alt sıraya al"
                :disabled="!canMoveCategory(cat, 1)"
              >
                ↓
              </button>
        </div>
        <button
          v-if="isAuthed"
          class="delete-badge"
          @click.stop="deleteCategory(cat)"
          aria-label="Kategoriyi sil"
        >
          ×
        </button>
        <div class="overlay">
          <h2>{{ cat.title }}</h2>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.home {
  padding: 16px;
  position: relative;
  overflow: hidden;
  min-height: 100%;
}
.decor-top,
.decor-bottom {
  position: absolute;
  left: -10%;
  width: 120%;
  pointer-events: none;
  z-index: 0;
}
.decor-top {
  top: -40px;
  height: 180px;
  background:
    radial-gradient(120px 60px at 20% 30%, rgba(73, 182, 255, 0.35), transparent 70%),
    radial-gradient(90px 50px at 60% 20%, rgba(47, 123, 255, 0.3), transparent 70%),
    radial-gradient(140px 70px at 80% 60%, rgba(73, 182, 255, 0.25), transparent 70%);
  filter: blur(0.2px);
}
.decor-top::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='180' viewBox='0 0 1200 180' fill='none'><g stroke='rgba(73,182,255,0.45)' stroke-width='3' stroke-linecap='round'><path d='M120 60c22-10 48-10 70 0c-22 10-48 10-70 0z'/><circle cx='190' cy='60' r='2' fill='rgba(73,182,255,0.7)'/><path d='M520 40c18-8 40-8 58 0c-18 8-40 8-58 0z'/><circle cx='570' cy='40' r='2' fill='rgba(73,182,255,0.7)'/><path d='M860 70c26-12 56-12 82 0c-26 12-56 12-82 0z'/><circle cx='932' cy='70' r='2' fill='rgba(73,182,255,0.7)'/></g><path d='M0 140c80-30 160-30 240 0s160 30 240 0s160-30 240 0s160 30 240 0s160-30 240 0' stroke='rgba(73,182,255,0.2)' stroke-width='2' fill='none'/></svg>");
  background-repeat: no-repeat;
  background-position: center top;
  opacity: 0.9;
}
.decor-bottom {
  top: 35%;
  bottom: 0;
  height: auto;
  background:
    radial-gradient(160px 100px at 15% 80%, rgba(11, 35, 66, 0.9), transparent 70%),
    radial-gradient(220px 120px at 80% 70%, rgba(15, 47, 89, 0.9), transparent 70%),
    linear-gradient(180deg, transparent 0%, rgba(6, 18, 37, 0.85) 55%, rgba(6, 18, 37, 1) 100%);
}
.decor-bottom::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='260' viewBox='0 0 1200 260' fill='none'><g stroke='rgba(73,182,255,0.25)' stroke-width='3' stroke-linecap='round'><path d='M140 200c0-20 10-34 26-44' /><path d='M140 200c8-18 22-30 44-38' /><path d='M300 220c0-24 12-42 30-54' /><path d='M300 220c10-20 28-34 54-42' /><path d='M980 210c0-22 12-38 28-50' /><path d='M980 210c10-18 26-30 50-38' /></g><path d='M0 120c100 30 200 30 300 0s200-30 300 0s200 30 300 0s200-30 300 0' stroke='rgba(73,182,255,0.15)' stroke-width='2' fill='none'/></svg>");
  background-repeat: repeat-y;
  background-size: 1200px 260px;
  background-position: center top;
  opacity: 0.9;
}
.hero {
  margin: 10px 0 22px;
  position: relative;
  z-index: 1;
}
.brand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.brand-logo {
  display: block;
  width: 120px;
  height: auto;
}
.subtitle {
  color: var(--muted);
  margin: 6px 0 0;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
}
.hero-actions {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.menu-link {
  background: rgba(8, 18, 33, 0.7);
  color: var(--text);
  border: 1px solid rgba(73, 182, 255, 0.35);
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
}
.about-link {
  margin-top: 0;
  background: linear-gradient(135deg, var(--accent-strong), var(--accent));
  color: #041327;
  border: none;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(3, 12, 30, 0.4);
}
.category-card {
  position: relative;
  z-index: 1;
  height: 180px;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 18px;
  cursor: pointer;
  border: 1px solid rgba(73, 182, 255, 0.18);
  box-shadow: 0 18px 40px rgba(2, 10, 24, 0.55);
}
.category-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.order-badges {
  position: absolute;
  left: 10px;
  top: 10px;
  display: inline-flex;
  gap: 6px;
  z-index: 2;
}
.order-badge {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(73, 182, 255, 0.45);
  background: rgba(8, 18, 33, 0.75);
  color: var(--text);
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
  padding: 0;
}
.order-badge:disabled {
  opacity: 0.4;
  cursor: default;
}
.delete-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 92, 92, 0.5);
  background: rgba(8, 18, 33, 0.7);
  color: #ff8a8a;
  font-size: 18px;
  line-height: 24px;
  cursor: pointer;
  z-index: 2;
}
.state {
  color: var(--muted);
  padding: 10px 0 30px;
}
.overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(6, 18, 37, 0.1),
    rgba(6, 18, 37, 0.75) 55%,
    rgba(7, 22, 45, 0.95)
  );
  display: flex;
  align-items: flex-end;
  padding: 16px;
}
.overlay h2 {
  color: var(--text);
  font-size: 24px;
}
</style>
